/**
 * Company identity, the compliance notice, and the policy documents.
 *
 * Our payment partner's onboarding asks the site to show the company name as evidence of
 * ownership plus the required policies (Terms, Privacy, Refund, About, Contact). The policies
 * come in TWO audience sets — customers read one agreement, vendors another — so a surface
 * picks its set with `policiesFor(audience)`:
 *   - /customer/profile → 'customer'
 *   - /vendor/profile   → 'vendor'
 *   - /legal (public)   → both, behind a toggle
 *
 * Wording is the business's own, supplied 2026-09-21; do not rewrite it here. Square brackets
 * (e.g. `[X] days`) are values the business has still to fix — they render literally on purpose.
 */

import { CUSTOMER_POLICIES } from './legal.customer';
import { VENDOR_POLICIES } from './legal.vendor';

export const COMPANY = {
  /** Name shown in the site-wide © line. */
  displayName: 'Inspiration Valley',
  /** Registered name, as on the SSM certificate. */
  legalName: 'Inspiration Valley Enterprise',
  /** Business registration number. */
  ssm: '002746500-T',
  brand: 'Trexa',
  addressLines: ['Plaza Damas 1, Taman Sri Hartamas', '50480 Kuala Lumpur'],
  email: 'support@ivalley.my',
  phone: '+60 18-703 5797',
  /** Digits-only, for wa.me / tel: links. */
  phoneDigits: '60187035797',
} as const;

/** The site-wide footer line: "© 2026 Inspiration Valley. All rights reserved." */
export function copyrightLine(): string {
  return `© ${new Date().getFullYear()} ${COMPANY.displayName}. All rights reserved.`;
}

/**
 * The compliance notice, in the wording of our payment partner's onboarding message — flipped
 * from "your company / your business" to our own voice. It heads the legal block.
 */
export const LEGAL_NOTICE = {
  title: 'Legal & policies',
  /** Evidence of website ownership — the company name, stated plainly. */
  ownership: `This website belongs to ${COMPANY.legalName}${COMPANY.ssm ? ` (SSM: ${COMPANY.ssm})` : ''}.`,
  policiesIntro: 'This website includes the required information and policies:',
} as const;

/** A policy is a list of blocks so headings, lettered clauses and rate tables all render. */
export type LegalBlock =
  /** Clause heading, e.g. "4. Bookings". */
  | { k: 'h'; t: string }
  /** Paragraph. `tight` closes the gap to the next line — used for address blocks. */
  | { k: 'p'; t: string; tight?: boolean }
  /** List item. `m` is the marker to print (e.g. "a)"); omitted renders a dot. */
  | { k: 'li'; t: string; m?: string }
  /** A labelled case with its outcomes — refund tiers, payout triggers. */
  | { k: 'tier'; t: string; rows: [string, string][] };

export interface LegalPolicy {
  key: string;
  title: string;
  /** One-line summary, shown on the collapsed row. */
  sub: string;
  blocks: LegalBlock[];
}

export type LegalAudience = 'customer' | 'vendor';

/** The five policies for one audience, in the order the onboarding message lists them. */
export function policiesFor(audience: LegalAudience): readonly LegalPolicy[] {
  return audience === 'vendor' ? VENDOR_POLICIES : CUSTOMER_POLICIES;
}

export { CUSTOMER_POLICIES, VENDOR_POLICIES };
